import React, { useState, useMemo } from 'react';
import { Table, Button, FormControl, Modal } from 'react-bootstrap';
import './TableSemanticTokens.css';


function TableActionTokens(props) {
  
    const { data, action, action_name, title, selectedRowIndex, setSelectedRowIndex, updateSemanticWord, onDataUpdate } = props;

    const [newWeight, setNewWeight] = useState(null);
    const [editingWeightRowId, setEditingWeightRowId] = useState(null);
    const [editingWordRowId, setEditingWordRowId] = useState(null);
    const [editingWord, setEditingWord] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const handleWeightChange = (e) => {
        setNewWeight(e.target.value);
    };

    const handleWordChange = (e) => {
        setEditingWord(e.target.value);
    };

    const commitWeight = async (row) => {
        const floatVal = parseFloat(newWeight);
        if (!isNaN(floatVal) && floatVal !== row.weight) {
            try {
                const result = await updateSemanticWord(row.id, floatVal); // вызываем callback для запроса на бек
                row.weight = result.weight;
                // Обновляем данные в родительском компоненте
                if (onDataUpdate) {
                    onDataUpdate();
                }
            } catch (error) {
                setErrorMessage(error.message);
                setShowErrorModal(true);
            }
        }
        setEditingWeightRowId(null);
        setNewWeight(null);
    };

    const commitWord = async (row) => {
        const newWord = editingWord?.trim();
        // Закрываем поле редактирования в любом случае
        setEditingWordRowId(null);
        const previousWord = editingWord;
        setEditingWord(null);
        
        if (newWord && newWord !== row.word) {
            try {
                const result = await updateSemanticWord(row.id, null, newWord);
                // Обновляем данные в строке только при успехе
                row.word = result.word;
                row.lemma = result.lemma;
                // Обновляем данные в родительском компоненте
                if (onDataUpdate) {
                    onDataUpdate();
                }
            } catch (error) {
                // При ошибке показываем модальное окно, но не обновляем данные
                setErrorMessage(error.message);
                setShowErrorModal(true);
                // Восстанавливаем исходное значение в поле (хотя поле уже закрыто)
                // Данные в таблице остаются прежними, так как мы не обновили row.word
            }
        }
    };

    const handleKeyDown = (e, row, type) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (type === 'weight') {
                commitWeight(row);
            } else if (type === 'word') {
                commitWord(row);
            }
        } else if (e.key === 'Escape') {
            if (type === 'weight') {
                setEditingWeightRowId(null);
                setNewWeight(null);
            } else if (type === 'word') {
                setEditingWordRowId(null);
                setEditingWord(null);
            }
        }
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const sortedData = useMemo(() => {
        if (sortConfig.key === null) {
            return [...data];
        }

        return [...data].sort((a, b) => {
            let aValue, bValue;
            
            switch (sortConfig.key) {
                case 'word':
                    aValue = (a.word || '').toLowerCase();
                    bValue = (b.word || '').toLowerCase();
                    break;
                case 'lemma':
                    aValue = (a.lemma || '').toLowerCase();
                    bValue = (b.lemma || '').toLowerCase();
                    break;
                case 'weight':
                    aValue = parseFloat(a.weight) || 0;
                    bValue = parseFloat(b.weight) || 0;
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [data, sortConfig]);

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return ' ↕';
        }
        return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
    };

    return (
        <>
            <Table striped bordered hover className="combination-table">
                <thead onClick={() => setSelectedRowIndex(null)} style={{ cursor: 'default' }}>
                    <tr>
                        <th 
                            onClick={(e) => { e.stopPropagation(); handleSort('word'); }} 
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                        >
                            { title }{getSortIcon('word')}
                        </th>
                        <th 
                            onClick={(e) => { e.stopPropagation(); handleSort('lemma'); }} 
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                        >
                            Лемма{getSortIcon('lemma')}
                        </th>
                        <th 
                            onClick={(e) => { e.stopPropagation(); handleSort('weight'); }} 
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                        >
                            Вес{getSortIcon('weight')}
                        </th>
                        <th>Действие</th>
                    </tr>
                </thead>
                <tbody>
                {sortedData.map((row, index) => (
                    <tr 
                        key={ row.id }
                        className={index === selectedRowIndex ? 'table-active-row' : ''}
                        onClick={() => setSelectedRowIndex(index)}
                        style={{ cursor: 'pointer' }}
                    >
                    <td 
                        onDoubleClick={(e) => { 
                            e.stopPropagation(); 
                            setEditingWordRowId(row.id); 
                            setEditingWord(row.word); 
                        }}
                    >
                        {editingWordRowId === row.id ? (
                            <FormControl
                                type="text"
                                value={editingWord || ''}
                                autoFocus
                                onChange={handleWordChange}
                                onBlur={() => commitWord(row)}
                                onKeyDown={(e) => handleKeyDown(e, row, 'word')}
                                style={{ width: '100%' }}
                            />
                        ) : (
                            row.word
                        )}
                    </td>
                    <td>{ row.lemma }</td>
                    <td onClick={(e) => { e.stopPropagation(); setEditingWeightRowId(row.id); setNewWeight(row.weight); }}>
                        {editingWeightRowId === row.id ? (
                            <FormControl
                                type="number"
                                //step="0.1"
                                step="any"
                                value={newWeight}
                                autoFocus
                                onChange={handleWeightChange}
                                onBlur={() => commitWeight(row)}
                                onKeyDown={(e) => handleKeyDown(e, row, 'weight')}
                                style={{ width: '100px' }}  // фиксированная ширина
                            />
                        ) : (
                            row.weight
                        )}
                    </td>
                    <td style={{ whiteSpace: 'nowrap', width: '1%' }}>
                        <Button
                            variant="danger"
                            size="sm"
                            onClick={(e) => {
                                e.stopPropagation();
                                // Передаем ID элемента вместо индекса
                                action(row.id);
                            }}
                        >
                        {action_name}
                        </Button>
                    </td>
                    </tr>
                ))}
                </tbody>
            </Table>
            <p>Всего токенов: {data.length}</p>
            
            <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Ошибка</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {errorMessage}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowErrorModal(false)}>
                        Закрыть
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default TableActionTokens;